import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { playBeep } from "../lib/sound";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Minus, Trash2, Search, ConciergeBell, Settings2, QrCode, RefreshCw, X,
  ChefHat, CheckCircle2, Utensils, Receipt, Loader2, Send, History, AlertTriangle, Banknote, CreditCard, FileText,
  ChevronUp, ChevronDown,
} from "lucide-react";
import OrderHistoryDialog from "../components/orders/OrderHistoryDialog";
import StornoDialog from "../components/orders/StornoDialog";

const GOLD = "#C9A24B";

const STATUS_META = {
  free: { label: "Libero", cls: "bg-secondary text-muted-foreground" },
  occupied: { label: "Occupato", cls: "bg-zinc-200 text-zinc-700" },
  ordered: { label: "Ordinato", cls: "bg-amber-100 text-amber-700" },
  preparing: { label: "In preparazione", cls: "bg-blue-100 text-blue-700" },
  ready: { label: "Pronto", cls: "bg-emerald-100 text-emerald-700" },
  served: { label: "Servito · da pagare", cls: "bg-[#C9A24B]/20 text-[#7a5f1f]" },
};

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "free", label: "Liberi" },
  { key: "occupied", label: "Occupati" },
  { key: "toprep", label: "Da preparare" },
  { key: "ready", label: "Pronti" },
  { key: "topay", label: "Da pagare" },
];

const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lowStock, setLowStock] = useState([]);
  const [activeStorno, setActiveStorno] = useState(null);
  const [foodCost, setFoodCost] = useState(null);
  const [contoView, setContoView] = useState(null);   // "preconto" | "preventivo" | null
  const [incassaOpen, setIncassaOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // order dialog
  const [openTable, setOpenTable] = useState(null);
  const [order, setOrder] = useState(null);      // active order doc or null
  const [cart, setCart] = useState([]);           // [{dish_id,name,unit_price,quantity,notes}]
  const [orderNotes, setOrderNotes] = useState("");
  const [term, setTerm] = useState("");
  const [busy, setBusy] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(1);

  // tables management
  const [mgmtOpen, setMgmtOpen] = useState(false);
  const [tEdit, setTEdit] = useState(null);
  const dialogOpenRef = useRef(false);
  const readyRef = useRef(null);
  const submitKeyRef = useRef(null);
  const dirtyRef = useRef(false);

  const loadTables = async () => {
    try {
      const { data } = await api.get("/tables");
      const readyIds = data.filter((t) => t.current_order?.status === "ready").map((t) => t.current_order.id);
      if (readyRef.current) {
        data.forEach((t) => {
          const oid = t.current_order?.id;
          if (t.current_order?.status === "ready" && oid && !readyRef.current.has(oid)) {
            playBeep(); toast.success(`${t.name} pronto da servire`);
          }
        });
      }
      readyRef.current = new Set(readyIds);
      setTables(data);
    } catch (e) { /* silent on poll */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadTables();
    api.get("/dishes").then(({ data }) => setDishes(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  // polling every 3s (real-time fallback)
  useEffect(() => {
    const id = setInterval(() => { loadTables(); if (dialogOpenRef.current && order?.id) refreshOrder(order.id); }, 3000);
    return () => clearInterval(id);
  }, [order?.id]);

  useEffect(() => { dialogOpenRef.current = !!openTable; }, [openTable]);

  const refreshOrder = async (oid, syncCart = false) => {
    try {
      const { data } = await api.get(`/orders/${oid}`);
      setOrder(data);
      if (syncCart || !dirtyRef.current) {
        setCart((data.items || []).filter((i) => !i.voided).map((i) => ({ item_id: i.item_id, dish_id: i.dish_id, name: i.name, unit_price: i.unit_price, quantity: i.quantity, notes: i.notes || "", course: i.course || 1, fired: !!i.fired })));
        setCurrentCourse(Math.max(1, ...(data.items || []).map((i) => i.course || 1)));
        dirtyRef.current = false;
      }
    } catch (e) { /* ignore */ }
  };

  const shown = useMemo(() => {
    return tables.filter((t) => {
      if (!t.active) return false;
      const s = t.status || "free";
      if (filter === "all") return true;
      if (filter === "free") return s === "free";
      if (filter === "occupied") return s !== "free";
      if (filter === "toprep") return s === "ordered" || s === "preparing";
      if (filter === "ready") return s === "ready";
      if (filter === "topay") return s === "served";
      return true;
    });
  }, [tables, filter]);

  const openOrderDialog = async (t) => {
    setOpenTable(t); setTerm(""); setOrderNotes(""); setCurrentCourse(1); dirtyRef.current = false; submitKeyRef.current = null;
    if (t.current_order?.id) { await refreshOrder(t.current_order.id, true); }
    else { setOrder(null); setCart([]); }
  };
  const closeDialog = () => { setOpenTable(null); setOrder(null); setCart([]); setCurrentCourse(1); dirtyRef.current = false; submitKeyRef.current = null; };

  const firedCourses = useMemo(() => {
    const s = new Set();
    (cart || []).forEach((i) => { if (i.fired) s.add(i.course || 1); });
    const oc = order?.courses || {};
    Object.keys(oc).forEach((k) => { if (oc[k]?.status === "fired") s.add(Number(k)); });
    return s;
  }, [cart, order]);
  const courseNums = useMemo(() => {
    const s = new Set(cart.map((i) => i.course || 1));
    s.add(currentCourse);
    return Array.from(s).sort((a, b) => a - b);
  }, [cart, currentCourse]);
  const maxCourse = () => cart.reduce((m, i) => Math.max(m, i.course || 1), currentCourse);

  const addToCart = (d) => {
    dirtyRef.current = true;
    setCart((c) => {
      const ex = c.find((i) => i.dish_id === d.id && (i.course || 1) === currentCourse && !i.fired);
      if (ex) return c.map((i) => (i === ex ? { ...i, quantity: i.quantity + 1 } : i));
      return [...c, { dish_id: d.id, name: d.name, unit_price: d.price, quantity: 1, notes: "", course: currentCourse, fired: false }];
    });
  };
  const setQty = (idx, q) => { dirtyRef.current = true; setCart((c) => c.map((i, k) => (k === idx ? { ...i, quantity: Math.max(1, q) } : i))); };
  const setNote = (idx, v) => { dirtyRef.current = true; setCart((c) => c.map((i, k) => (k === idx ? { ...i, notes: v } : i))); };
  const removeItem = (idx) => { dirtyRef.current = true; setCart((c) => c.filter((_, k) => k !== idx)); };
  const cancelFired = (idx) => { if (window.confirm("Annullare questo prodotto già inviato? Verrà stampata una comanda di ANNULLAMENTO alla conferma.")) removeItem(idx); };
  const addSbarra = () => setCurrentCourse(maxCourse() + 1);
  const moveItem = (idx, dir) => {
    dirtyRef.current = true;
    setCart((c) => {
      const it = c[idx];
      if (!it || it.fired) return c;
      const mx = c.reduce((m, i) => Math.max(m, i.course || 1), currentCourse);
      let t = (it.course || 1) + dir;
      while (t >= 1 && t <= mx && firedCourses.has(t)) t += dir;
      if (t < 1 || t > mx) return c;
      return c.map((i, k) => (k === idx ? { ...i, course: t } : i));
    });
  };
  const fireCourse = async (cn) => {
    if (!order?.id) return;
    setBusy(true);
    try { await api.post(`/orders/${order.id}/courses/${cn}/fire`); toast.success(`Uscita ${cn} inviata in produzione`); await refreshOrder(order.id, true); loadTables(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cartTotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);

  const sendOrder = async () => {
    const payloadItems = cart.map((i) => ({ item_id: i.item_id, dish_id: i.dish_id, quantity: i.quantity, notes: i.notes, course: i.course || 1 }));
    if (!payloadItems.length && !order?.id) { toast.error("Aggiungi almeno un prodotto"); return; }
    if (!submitKeyRef.current) submitKeyRef.current = (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random());
    setBusy(true);
    try {
      const body = { order_id: order?.id || null, table_id: openTable.id, notes: orderNotes, idempotency_key: submitKeyRef.current, items: payloadItems };
      const { data } = await api.post("/orders/waiter-submit", body);
      setOrder(data);
      setCart((data.items || []).filter((i) => !i.voided).map((i) => ({ item_id: i.item_id, dish_id: i.dish_id, name: i.name, unit_price: i.unit_price, quantity: i.quantity, notes: i.notes || "", course: i.course || 1, fired: !!i.fired })));
      setCurrentCourse(Math.max(1, ...(data.items || []).map((i) => i.course || 1)));
      dirtyRef.current = false;
      submitKeyRef.current = null;
      toast.success(order?.id ? "Comanda aggiornata" : `Comanda inviata · ${data.order_code}`);
      loadTables();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (path, okMsg) => {
    if (!order?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/${path}`);
      toast.success(okMsg);
      if (["close", "cancel", "reverse"].includes(path)) { closeDialog(); loadTables(); }
      else { setOrder(data); loadTables(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => { setFoodCost(null); setContoView(null); setIncassaOpen(false); }, [order?.id]);
  const loadFoodCost = async () => {
    if (!order?.id) return;
    try { const { data } = await api.get(`/orders/${order.id}/food-cost`); setFoodCost(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // INCASSA (pilot): registra pagamento gestionale (cash|card) e chiude il conto → tavolo libero.
  // Nessuna emissione fiscale: lo scontrino resta esterno (EasyEvolver).
  const incassa = async (method) => {
    if (!order?.id) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/payment`, { payment_status: "paid", payment_method: method });
      await api.post(`/orders/${order.id}/close`);
      toast.success(`Incassato ${eur(order.total)} · ${method === "cash" ? "Contanti" : "Pagamento elettronico"} · conto chiuso`);
      setIncassaOpen(false); closeDialog(); loadTables();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const closeOrder = async () => {
    if (!order?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/close`);
      toast.success("Ordine chiuso · vendita registrata");
      const low = data?.deductions?.low_stock || [];
      if (low.length) { setLowStock(low); toast.warning(`Scorta bassa: ${low.join(", ")}`); }
      closeDialog(); loadTables();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filteredDishes = useMemo(() => {
    const t = term.trim().toLowerCase();
    const list = dishes.filter((d) => d.available !== false && (!t || d.name.toLowerCase().includes(t)));
    return list;
  }, [dishes, term]);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Servizio</p>
          <h1 className="font-dash text-3xl font-semibold flex items-center gap-2"><ConciergeBell className="w-7 h-7" style={{ color: GOLD }} /> Tavoli & Ordini</h1>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setHistoryOpen(true)} data-testid="order-history-button">
              <History className="w-4 h-4 mr-1" /> Storico ordini
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => setMgmtOpen(true)} data-testid="manage-tables-button">
              <Settings2 className="w-4 h-4 mr-1" /> Gestisci tavoli
            </Button>
          </div>
        )}
      </div>

      {lowStock.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="low-stock-banner">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Scorta bassa dopo lo scarico: <b>{lowStock.join(", ")}</b></span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/magazzino")} data-testid="low-stock-open">Apri magazzino</Button>
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setLowStock([])} data-testid="low-stock-dismiss">Ignora</Button>
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="orders-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === f.key ? "text-white border-transparent" : "text-muted-foreground border-border hover:border-foreground/40"}`}
            style={filter === f.key ? { backgroundColor: "#14110E" } : {}}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : shown.length === 0 ? (
        <Card className="p-10 rounded-xl text-center text-muted-foreground" data-testid="orders-empty">
          {tables.length === 0 ? "Nessun tavolo configurato. " : "Nessun tavolo per questo filtro."}
          {canManage && tables.length === 0 && <span className="underline cursor-pointer ml-1" onClick={() => setMgmtOpen(true)}>Crea il primo tavolo</span>}
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tables-grid">
          {shown.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.free;
            return (
              <Card key={t.id} onClick={() => openOrderDialog(t)} data-testid={`table-card-${t.number}`}
                className="p-4 rounded-2xl cursor-pointer hover:shadow-md transition-shadow border-border active:scale-[0.98]">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-dash text-2xl font-semibold leading-none">{t.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t.seats} posti{t.room ? ` · ${t.room}` : ""}</p>
                  </div>
                  {t.status === "ready" && <ChefHat className="w-4 h-4 text-emerald-600" />}
                  {t.status === "served" && <Receipt className="w-4 h-4" style={{ color: GOLD }} />}
                </div>
                <Badge className={`mt-3 rounded-full font-medium ${meta.cls}`} data-testid={`table-status-${t.number}`}>{meta.label}</Badge>
                {t.current_order && (
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t.current_order.items_count} art.</span>
                    <span className="font-semibold">{eur(t.current_order.total)}</span>
                  </div>
                )}
                <Button size="sm" variant="ghost" className="w-full mt-2 rounded-full text-primary" data-testid={`open-table-${t.number}`}>Apri</Button>
              </Card>
            );
          })}
        </div>
      )}

      {/* ORDER DIALOG */}
      <Dialog open={!!openTable} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="w-[96vw] max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="order-dialog">
          {openTable && (
            <div className="flex flex-col">
              <DialogHeader className="p-5 pb-3 border-b border-border sticky top-0 bg-background z-10">
                <DialogTitle className="flex items-center justify-between">
                  <span>{openTable.name} {order?.order_code && <span className="text-sm text-muted-foreground font-normal ml-2">{order.order_code}</span>}</span>
                  {order && <Badge className={`rounded-full ${(STATUS_META[order.status] || {}).cls || "bg-secondary"}`}>{(STATUS_META[order.status] || {}).label || order.status}</Badge>}
                </DialogTitle>
                <DialogDescription className="text-xs">{openTable.seats} posti{openTable.room ? ` · ${openTable.room}` : ""} · Prezzi ricalcolati dal server</DialogDescription>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-0">
                {/* Menu picker */}
                <div className="p-4 border-r border-border">
                  <div className="relative mb-3">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Cerca prodotto…" className="pl-9 rounded-full" data-testid="dish-search" />
                  </div>
                  <div className="space-y-1 max-h-[46vh] overflow-y-auto pr-1">
                    {filteredDishes.map((d) => (
                      <button key={d.id} onClick={() => addToCart(d)} data-testid={`add-dish-${d.id}`}
                        className="w-full flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-secondary text-left transition-colors">
                        <span className="text-sm truncate">{d.name}</span>
                        <span className="text-sm font-medium whitespace-nowrap" style={{ color: GOLD }}>{eur(d.price)}</span>
                      </button>
                    ))}
                    {filteredDishes.length === 0 && <p className="text-sm text-muted-foreground p-3 text-center">Nessun prodotto</p>}
                  </div>
                </div>

                {/* Cart */}
                <div className="p-4 flex flex-col pb-24">
                  <div className="flex-1 space-y-3 max-h-[42vh] overflow-y-auto pr-1" data-testid="order-cart">
                    {cart.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aggiungi prodotti dal menu</p>}
                    {courseNums.map((cn) => {
                      const items = cart.map((i, idx) => ({ ...i, _idx: idx })).filter((i) => (i.course || 1) === cn);
                      const fired = firedCourses.has(cn);
                      const saved = order?.courses?.[String(cn)];
                      const canFire = order?.id && saved && saved.status === "waiting" && items.length > 0 && !dirtyRef.current;
                      if (items.length === 0 && cn !== currentCourse) return null;
                      return (
                        <div key={cn} data-testid={`cart-course-${cn}`}>
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                              <span className="h-px w-4 bg-border" /> Uscita {cn}
                              {fired && <span className="text-emerald-600">· inviata</span>}
                              {cn === currentCourse && !fired && <span className="text-primary">· in corso</span>}
                            </div>
                            {canFire && (
                              <Button size="sm" className="rounded-full h-7" style={{ backgroundColor: GOLD, color: "#14110E" }} onClick={() => fireCourse(cn)} disabled={busy} data-testid={`fire-course-${cn}`}>
                                <Send className="w-3.5 h-3.5 mr-1" /> Manda uscita {cn}
                              </Button>
                            )}
                          </div>
                          {items.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic pb-1">Aggiungi prodotti a questa uscita…</p>
                          ) : items.map((i) => (
                            <div key={i._idx} className={`rounded-lg border p-2 mb-1.5 ${i.fired ? "border-emerald-200 bg-emerald-50/40" : "border-border"}`} data-testid={`cart-item-${i._idx}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium truncate flex-1">{i.name}{i.fired && <span className="ml-1 text-[10px] uppercase text-emerald-600">inviato</span>}</span>
                                <span className="text-sm whitespace-nowrap">{eur(i.unit_price * i.quantity)}</span>
                              </div>
                              {i.fired ? (
                                <div className="flex items-center justify-between mt-1.5">
                                  <span className="text-sm font-medium">Qtà {i.quantity}</span>
                                  <Button size="sm" variant="ghost" className="h-8 text-destructive rounded-full" onClick={() => cancelFired(i._idx)} data-testid={`cancel-fired-${i._idx}`}><X className="w-3.5 h-3.5 mr-1" /> Annulla</Button>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mt-1.5">
                                    <div className="flex items-center gap-1.5">
                                      <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => setQty(i._idx, i.quantity - 1)} data-testid={`qty-minus-${i._idx}`}><Minus className="w-4 h-4" /></Button>
                                      <span className="w-7 text-center text-base font-semibold" data-testid={`qty-val-${i._idx}`}>{i.quantity}</span>
                                      <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => setQty(i._idx, i.quantity + 1)} data-testid={`qty-plus-${i._idx}`}><Plus className="w-4 h-4" /></Button>
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Sposta uscita su" onClick={() => moveItem(i._idx, -1)} data-testid={`move-up-${i._idx}`}><ChevronUp className="w-4 h-4" /></Button>
                                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Sposta uscita giù" onClick={() => moveItem(i._idx, 1)} data-testid={`move-down-${i._idx}`}><ChevronDown className="w-4 h-4" /></Button>
                                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeItem(i._idx)} data-testid={`remove-item-${i._idx}`}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                  </div>
                                  <Input value={i.notes} onChange={(e) => setNote(i._idx, e.target.value)} placeholder="Nota riga (es. senza ghiaccio)" className="mt-1.5 h-9 text-xs" data-testid={`item-note-${i._idx}`} />
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    <Button variant="outline" className="w-full rounded-full border-dashed mt-1 h-11 font-semibold" onClick={addSbarra} data-testid="add-sbarra-button">
                      + Sbarra / Uscita successiva
                    </Button>
                  </div>
                  <Input value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} placeholder="Note ordine" className="mt-3" data-testid="order-notes" />
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <span className="text-sm text-muted-foreground">Totale</span>
                    <span className="font-dash text-xl font-semibold" data-testid="order-total">{eur(order?.total ?? cartTotal)}</span>
                  </div>
                  {isAdmin && order?.id && (
                    <div className="mt-3 pt-3 border-t border-border" data-testid="order-food-cost">
                      {!foodCost ? (
                        <Button variant="outline" size="sm" className="rounded-full" onClick={loadFoodCost} data-testid="load-food-cost">Mostra food cost</Button>
                      ) : (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between"><span className="text-muted-foreground">Ricavi</span><span data-testid="fc-revenue">{eur(foodCost.revenue)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Costo teorico</span><span data-testid="fc-cost">{eur(foodCost.theoretical_cost)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Margine lordo teorico</span><span className="text-[hsl(var(--success))]" data-testid="fc-margin">{eur(foodCost.gross_margin)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Food cost %</span><span data-testid="fc-pct">{foodCost.food_cost_pct}%</span></div>
                        </div>
                      )}
                    </div>
                  )}
                  <Button onClick={sendOrder} disabled={busy} className="rounded-full mt-3 h-12 text-base font-semibold" data-testid="send-order-button">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" /> {order?.id ? "AGGIORNA ORDINE" : "INVIA ORDINE"}</>}
                  </Button>
                </div>
              </div>

              {order?.id && (order.payment_status === "paid" || order.status === "closed") && (
                <div className="px-4 pb-2 border-t border-border pt-3 text-sm" data-testid="order-payment">
                  <div className="flex justify-between"><span className="text-muted-foreground">Pagamento</span><span data-testid="pay-status">{order.payment_status}{order.payment_method ? ` · ${order.payment_method === "cash" ? "Contanti" : order.payment_method === "card" ? "Elettronico" : order.payment_method}` : ""}</span></div>
                  {order.paid_at && <div className="flex justify-between"><span className="text-muted-foreground">Incassato il</span><span>{new Date(order.paid_at).toLocaleString("it-IT")}</span></div>}
                </div>
              )}

              {/* Azioni conto (pilot): Conto · Preventivo · Incassa */}
              {order?.id && !["closed", "cancelled"].includes(order.status) && (
                <div className="p-4 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-2 sticky bottom-0 bg-background" data-testid="order-actions">
                  <Button variant="outline" className="rounded-full" onClick={() => setContoView("preconto")} disabled={busy || !(order.items || []).length} data-testid="action-conto"><Receipt className="w-4 h-4 mr-1" /> Conto</Button>
                  <Button variant="outline" className="rounded-full" onClick={() => setContoView("preventivo")} disabled={busy || !(order.items || []).length} data-testid="action-preventivo"><FileText className="w-4 h-4 mr-1" /> Preventivo</Button>
                  <Button className="rounded-full" style={{ backgroundColor: "#16a34a", color: "white" }} onClick={() => setIncassaOpen(true)} disabled={busy || !(order.items || []).filter((i) => !i.voided).length} data-testid="action-incassa"><Banknote className="w-4 h-4 mr-1" /> Incassa</Button>
                  <Button variant="ghost" className="rounded-full text-destructive" onClick={() => act("cancel", "Ordine annullato")} disabled={busy} data-testid="action-cancel"><X className="w-4 h-4 mr-1" /> Annulla</Button>
                </div>
              )}
              {order?.id && order.status === "closed" && canManage && !order.is_voided && (
                <div className="p-4 border-t border-border sticky bottom-0 bg-background">
                  <Button variant="ghost" className="rounded-full text-destructive" onClick={() => setActiveStorno(order)} disabled={busy} data-testid="action-reverse"><X className="w-4 h-4 mr-1" /> Storna vendita</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {canManage && <TablesManager open={mgmtOpen} onOpenChange={setMgmtOpen} tables={tables} reload={loadTables} tEdit={tEdit} setTEdit={setTEdit} />}
      {/* PRECONTO / PREVENTIVO — documenti NON FISCALI, nessun pagamento, nessuna chiusura */}
      <Dialog open={!!contoView} onOpenChange={(o) => !o && setContoView(null)}>
        <DialogContent className="w-[95vw] max-w-sm" data-testid="conto-dialog">
          <DialogHeader>
            <DialogTitle>{contoView === "preventivo" ? "Preventivo" : "Preconto"}{openTable ? ` · ${openTable.name}` : ""}</DialogTitle>
            <DialogDescription className="text-xs font-semibold" style={{ color: GOLD }}>
              {contoView === "preventivo"
                ? "PREVENTIVO / NON FISCALE — simulazione, nessun incasso e nessuna chiusura"
                : "PRECONTO — documento NON FISCALE, il tavolo resta aperto"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 text-sm" data-testid="conto-body">
            {(order?.items || []).filter((i) => !i.voided).map((i, idx) => (
              <div key={idx} className="flex justify-between gap-2">
                <span className="truncate">{i.quantity}× {i.name}{i.notes ? <span className="text-muted-foreground text-xs"> · {i.notes}</span> : null}</span>
                <span className="whitespace-nowrap">{eur((i.unit_price || 0) * (i.quantity || 0))}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-border pt-2 mt-2 font-semibold text-base">
              <span>Totale</span><span data-testid="conto-total">{eur(order?.total ?? cartTotal)}</span>
            </div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setContoView(null)} data-testid="conto-close">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* INCASSA — scelta metodo obbligatoria; registra pagamento gestionale e chiude il conto */}
      <Dialog open={incassaOpen} onOpenChange={(o) => !o && setIncassaOpen(false)}>
        <DialogContent className="w-[95vw] max-w-sm" data-testid="incassa-dialog">
          <DialogHeader>
            <DialogTitle>Incassa {eur(order?.total)}</DialogTitle>
            <DialogDescription className="text-xs">Registrazione gestionale interna. Lo scontrino fiscale è emesso separatamente (EasyEvolver).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => incassa("cash")} disabled={busy} className="py-6 rounded-xl border border-border hover:bg-secondary flex flex-col items-center gap-2 disabled:opacity-50 active:scale-95 transition-transform" data-testid="incassa-cash">
              <Banknote className="w-6 h-6" style={{ color: GOLD }} /><span className="text-sm font-medium">Contanti</span>
            </button>
            <button onClick={() => incassa("card")} disabled={busy} className="py-6 rounded-xl border border-border hover:bg-secondary flex flex-col items-center gap-2 disabled:opacity-50 active:scale-95 transition-transform" data-testid="incassa-card">
              <CreditCard className="w-6 h-6" style={{ color: GOLD }} /><span className="text-sm font-medium">Pagamento elettronico</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {canManage && <OrderHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} tables={tables} />}
      {activeStorno && <StornoDialog open={!!activeStorno} onOpenChange={(o) => !o && setActiveStorno(null)} order={activeStorno} onDone={(d) => { setActiveStorno(null); closeDialog(); loadTables(); const low = d?.low_stock || []; if (low.length) setLowStock(low); }} />}
    </div>
  );
}

function TablesManager({ open, onOpenChange, tables, reload, tEdit, setTEdit }) {
  const empty = { number: "", name: "", room: "", seats: "2", active: true };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(tEdit ? { number: tEdit.number, name: tEdit.name, room: tEdit.room || "", seats: String(tEdit.seats ?? 2), active: tEdit.active } : empty); }, [tEdit, open]);

  const save = async () => {
    if (!form.number.trim()) { toast.error("Numero tavolo obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { ...form, seats: parseInt(form.seats, 10) || 1 };
      if (tEdit) await api.put(`/tables/${tEdit.id}`, payload);
      else await api.post("/tables", payload);
      toast.success(tEdit ? "Tavolo aggiornato" : "Tavolo creato");
      setTEdit(null); setForm(empty); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (t) => {
    if (!window.confirm(`Eliminare ${t.name}?`)) return;
    try { await api.delete(`/tables/${t.id}`); toast.success("Tavolo eliminato"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const regen = async (t) => {
    try { const { data } = await api.post(`/tables/${t.id}/regenerate-token`); toast.success("QR rigenerato"); reload(); copyQr({ ...t, public_token: data.public_token }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const qrUrl = (t) => `${window.location.origin}/menu?t=${t.public_token}`;
  const copyQr = (t) => { navigator.clipboard?.writeText(qrUrl(t)); toast.success("Link QR copiato"); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTEdit(null); }}>
      <DialogContent className="w-[96vw] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="tables-manager-dialog">
        <DialogHeader><DialogTitle>Gestione tavoli</DialogTitle><DialogDescription className="text-xs">Crea i tavoli e condividi il QR (link /menu?t=token) per l'ordine al tavolo.</DialogDescription></DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end rounded-xl border border-border p-3">
          <div className="col-span-1"><label className="text-xs text-muted-foreground">Numero</label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="table-number-input" /></div>
          <div className="col-span-1"><label className="text-xs text-muted-foreground">Nome</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tavolo 12" data-testid="table-name-input" /></div>
          <div className="col-span-1"><label className="text-xs text-muted-foreground">Sala</label><Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="Dehors" data-testid="table-room-input" /></div>
          <div className="col-span-1"><label className="text-xs text-muted-foreground">Posti</label><Input type="number" min="1" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} data-testid="table-seats-input" /></div>
          <Button onClick={save} disabled={busy} className="rounded-full" data-testid="save-table-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : tEdit ? "Salva" : "Aggiungi"}</Button>
        </div>
        {tEdit && <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setTEdit(null)}>Annulla modifica</Button>}

        <div className="space-y-2 mt-2">
          {tables.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid={`mgmt-table-${t.number}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{t.name} <span className="text-xs text-muted-foreground">· {t.seats}p{t.room ? ` · ${t.room}` : ""}</span></p>
                <p className="text-[11px] text-muted-foreground truncate">{qrUrl(t)}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Copia link QR" onClick={() => copyQr(t)} data-testid={`copy-qr-${t.number}`}><QrCode className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Rigenera QR" onClick={() => regen(t)} data-testid={`regen-qr-${t.number}`}><RefreshCw className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setTEdit(t)} data-testid={`edit-table-${t.number}`}><Settings2 className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(t)} data-testid={`del-table-${t.number}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          {tables.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nessun tavolo. Aggiungine uno sopra.</p>}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
